<template>
    <div id="test-app-dt">
        <div class="container">
            <!-- Basic Datatable Example -->
            <div class="example-section">
                <DataTable
                    :columns="userColumns"
                    :items="items"
                    :current-page="currentPage"
                    :total-pages="totalPages"
                    :enable-multi-select="false"
                    :enable-drag-drop="false"
                    @page-changed="handlePageChange"
                    @sort="onSort"
                >
                    <template #toolbar>
                        <div class="ppv2-flex ppv2-flex-wrap ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                            <div class="ppv2-flex-1"></div>

                            <!-- Search Input -->
                            <div class="ppv2-relative">
                                <Search v-model="searchQuery" :emitGlobal="false" @search-updated="onSearch" />
                            </div>
                        </div>
                    </template>

                    <!-- Custom slot for avatar -->
                    <template #column-avatar="{ item }">
                        <img :src="item.avatar || 'https://via.placeholder.com/40'" :alt="item.name" class="avatar" />
                    </template>

                    <!-- Custom slot for status -->
                    <template #column-status="{ item }">
                        <span :class="['status-badge', item.status]">
                            {{ item.status }}
                        </span>
                    </template>

                    <!-- Custom slot for actions -->
                    <template #column-actions="{ item }">
                        <div
                            class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-3"
                            :style="{ fontSize: normalizeFontSize(18) }"
                        >
                            <button
                                @click.stop="editUser(item)"
                                class="ppv2-p-1 ppv2-text-black hover:ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                                :title="$t('common.edit')"
                            >
                                <SquarePen :size="18" />
                            </button>
                            <button
                                @click.stop="deleteUser(item)"
                                class="ppv2-p-1 ppv2-text-[#FF9C9C] hover:ppv2-text-[#FF9C9C] hover:ppv2-bg-[#FF9C9C] hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                                :title="$t('common.delete')"
                            >
                                <Trash2 :size="18" />
                            </button>
                        </div>
                    </template>
                </DataTable>
            </div>
        </div>
    </div>
</template>

<script>
import DataTable from '../../Shared/DataTable.vue'
import Search from '../../V3/Experience/components/Search.vue'
import normalizeSize from '../../../utils/normalizeSize'
import { SquarePen, Trash2 } from 'lucide-vue'
import normalizeFontSize from '../../../utils/normalizeFontSize'

export default {
    name: 'App',
    props: ['user'],
    components: {
        DataTable,
        Search,
        SquarePen,
        Trash2,
    },
    data() {
        return {
            // Data Table State
            items: [],
            loading: false,
            currentPage: 1,
            perPage: 10,
            totalEntries: 0,
            searchQuery: '',
            sortKey: '',
            sortOrder: 'asc',
            searchTimeout: null,

            // API Configuration
            apiHeaders: {
                Authorization: 'Bearer your-token-here',
                'X-Requested-With': 'XMLHttpRequest',
            },

            // Users Table Configuration
            usersApiUrl: '/back-office/v2/customer/' + this.user.id + '/ajaxEmails',
            userColumns: [
                {
                    key: 'object',
                    label: this.$t('datatable_customer_email_v2_edit.subject'),
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'start',
                    label: this.$t('datatable_customer_email_v2_edit.sentDate'),
                    sortable: true,
                    type: 'date',
                },
            ],
        }
    },
    computed: {
        totalPages() {
            return Math.ceil(this.totalEntries / this.perPage)
        },
    },
    mounted() {
        this.fetchData()
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        async fetchData() {
            this.loading = true
            try {
                const params = {
                    page: this.currentPage,
                    per_page: this.perPage,
                    search: this.searchQuery,
                    sort_by: this.sortKey,
                    sort_order: this.sortOrder,
                }
                const response = await this.makeApiRequest(params)
                this.handleApiResponse(response)
            } catch (error) {
                this.$toast.error(error.response.data.message)
                this.onError(error)
            } finally {
                this.loading = false
            }
        },
        async makeApiRequest(params) {
            const queryString = new URLSearchParams(params).toString()
            const url = `${this.usersApiUrl}?${queryString}`

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...this.apiHeaders,
                },
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            return await response.json()
        },
        handleApiResponse(response) {
            let _response = response.data
            if (_response.data && Array.isArray(_response.data)) {
                this.items = _response.data
                this.totalEntries = _response.total || _response.data.length
                this.onDataLoaded({ data: this.items, total: this.totalEntries })
            } else if (Array.isArray(_response)) {
                this.items = _response
                this.totalEntries = _response.length
                this.onDataLoaded({ data: this.items, total: this.totalEntries })
            } else {
                this.items = []
                this.totalEntries = 0
            }
        },
        handlePageChange(page) {
            this.currentPage = page
            this.fetchData()
        },
        onSearch() {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => {
                this.currentPage = 1
                this.fetchData()
            }, 500)
        },
        onSort(key) {
            if (this.sortKey === key) {
                this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
            } else {
                this.sortKey = key
                this.sortOrder = 'asc'
            }
            this.currentPage = 1
            this.fetchData()
        },
        onDataLoaded(response) {
            console.log('Data loaded:', response)
            // Handle successful data loading
        },

        onError(error) {
            console.error('Datatable error:', error)
            // Show error message to user
            alert('Error loading data: ' + error.message)
        },

        editUser(user) {
            console.log('Edit user:', user)
            // Open edit modal or navigate to edit page
        },

        deleteUser(user) {
            if (confirm(this.$t('customers.confirmDelete', { name: user.name }))) {
                console.log('Delete user:', user)
                // Make API call to delete user
                // Then refresh the table
            }
        },
    },
}
</script>

<style scoped>
.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
}

.example-section {
    margin-bottom: 40px;
}

.example-section h2 {
    margin-bottom: 20px;
    color: #333;
}

.avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
}

.status-badge {
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
}

.status-badge.active {
    background-color: #d4edda;
    color: #155724;
}

.status-badge.inactive {
    background-color: #f8d7da;
    color: #721c24;
}

.status-badge.pending {
    background-color: #fff3cd;
    color: #856404;
}

.btn {
    padding: 6px 12px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 500;
    text-decoration: none;
    display: inline-block;
    margin-right: 4px;
    transition: all 0.2s;
}

.btn-sm {
    padding: 4px 8px;
    font-size: 11px;
}

.btn-primary {
    background-color: #007bff;
    color: white;
}

.btn-primary:hover {
    background-color: #0056b3;
}

.btn-danger {
    background-color: #dc3545;
    color: white;
}

.btn-danger:hover {
    background-color: #c82333;
}

.btn-secondary {
    background-color: #6c757d;
    color: white;
}

.btn-secondary:hover {
    background-color: #545b62;
}

.btn-outline-secondary {
    background-color: transparent;
    color: #6c757d;
    border: 1px solid #6c757d;
}

.btn-outline-secondary:hover {
    background-color: #6c757d;
    color: white;
}

.post-link {
    color: #007bff;
    text-decoration: none;
}

.post-link:hover {
    text-decoration: underline;
}

.table-actions {
    margin-top: 16px;
    display: flex;
    gap: 8px;
}

@media (max-width: 768px) {
    .container {
        padding: 10px;
    }

    .table-actions {
        flex-direction: column;
    }
}
</style>
